import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import api from 'api/client';

const borderColor = 'rgba(245, 158, 11, 0.35)';
const cardSx = { border: '1px solid', borderColor, borderRadius: 3, boxShadow: 'none' };
const actionButtonSx = {
  bgcolor: 'var(--fz-surface-1, #111827)',
  color: '#f59e0b',
  border: '1px solid #f59e0b',
  '&:hover': { bgcolor: 'var(--fz-surface-0, #0b1220)', borderColor: '#f59e0b' }
};

const quickLinks = [
  { title: 'Users', path: '/admin/users', description: 'Manage accounts and access.' },
  { title: 'Roles', path: '/admin/roles', description: 'Define and assign role templates.' },
  { title: 'Policies', path: '/admin/policies', description: 'Control scan execution policies.' },
  { title: 'Integrations', path: '/admin/integrations', description: 'Maintain external providers.' },
  { title: 'Nuclei Templates', path: '/admin/nuclei-templates', description: 'Manage custom vuln checks and AI generation.' },
  { title: 'Announcements', path: '/admin/announcements', description: 'Broadcast global messages.' },
  { title: 'Notification Rules', path: '/admin/notification-rules', description: 'Route events to users and connectors.' },
  { title: 'Artifacts', path: '/admin/artifacts', description: 'Review generated scanner artifacts.' },
  { title: 'Fingerprints', path: '/admin/fingerprints', description: 'Inspect deduplicated finding clusters.' },
  { title: 'Sessions', path: '/admin/system/sessions', description: 'Review active user sessions.' },
  { title: 'Maintenance', path: '/admin/system/maintenance', description: 'Control maintenance banner state.' },
  { title: 'System Health', path: '/admin/system/health', description: 'Inspect workers and engine health.' },
  { title: 'Audit Logs', path: '/admin/audit-logs', description: 'Review privileged operations.' }
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    users: 0,
    organizations: 0,
    scans_total: 0,
    scans_running: 0,
    integrations: 0,
    connectors: 0,
    announcements: 0,
    artifacts: 0,
    fingerprints: 0,
    notification_rules: 0
  });
  const [recentScans, setRecentScans] = useState([]);
  const [recentAudit, setRecentAudit] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [healthRes, scansRes, auditRes] = await Promise.all([
        api.get('/api/admin/system/health/'),
        api.get('/api/admin/scans/'),
        api.get('/api/admin/audit-logs/?limit=8')
      ]);
      setStats(healthRes.data?.kpis || {});
      const scanRows = Array.isArray(scansRes.data) ? scansRes.data : scansRes.data?.results || [];
      setRecentScans(scanRows.slice(0, 8));
      const auditRows = Array.isArray(auditRes.data) ? auditRes.data : auditRes.data?.results || [];
      setRecentAudit(auditRows.slice(0, 8));
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load admin dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Stack spacing={2.5}>
      <Card sx={cardSx}>
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Avatar sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#f59e0b', width: 42, height: 42 }}>
                <AdminPanelSettingsOutlinedIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Administrator Control Center
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Centralized governance for users, policies, integrations, and platform operations.
                </Typography>
              </Box>
            </Stack>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={load} disabled={loading} sx={{ border: '1px solid', borderColor }}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={2}>
        {[
          ['Users', stats.users, '#f59e0b'],
          ['Organizations', stats.organizations, '#22c55e'],
          ['Scans Total', stats.scans_total, '#0ea5e9'],
          ['Running', stats.scans_running, '#6366f1'],
          ['Integrations', stats.integrations, '#a855f7'],
          ['Connectors', stats.connectors, '#f97316'],
          ['Announcements', stats.announcements, '#e11d48'],
          ['Artifacts', stats.artifacts, '#0ea5e9'],
          ['Fingerprints', stats.fingerprints, '#22c55e'],
          ['Notif Rules', stats.notification_rules, '#f59e0b']
        ].map(([label, value, color]) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} key={label}>
            <Card sx={cardSx}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color }}>
                  {value || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Quick Access
              </Typography>
              <Grid container spacing={1.5}>
                {quickLinks.map((item) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={item.title}>
                    <Card sx={{ ...cardSx, bgcolor: 'rgba(17,24,39,0.02)' }}>
                      <CardActionArea onClick={() => navigate(item.path)}>
                        <CardContent sx={{ py: 1.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {item.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.description}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Recent Scans
              </Typography>
              <Stack spacing={1}>
                {recentScans.length === 0 ? (
                  <Alert severity="info">No scans available.</Alert>
                ) : (
                  recentScans.map((scan) => (
                    <Stack
                      key={scan.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ border: '1px solid rgba(245,158,11,0.2)', borderRadius: 2, px: 1.5, py: 1 }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {scan.scan_type} - {scan.target_value || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {scan.owner_email || '-'}
                        </Typography>
                      </Box>
                      <Chip size="small" label={scan.status} />
                    </Stack>
                  ))
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={cardSx}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Recent Audit Events
          </Typography>
          <Stack spacing={1}>
            {recentAudit.length === 0 ? (
              <Alert severity="info">No audit events available.</Alert>
            ) : (
              recentAudit.map((event) => (
                <Stack
                  key={event.id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ border: '1px solid rgba(245,158,11,0.2)', borderRadius: 2, px: 1.5, py: 1 }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {event.action}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {event.actor_email || '-'} • {event.message || '-'}
                    </Typography>
                  </Box>
                  <Chip size="small" label={event.status} />
                </Stack>
              ))
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default AdminDashboard;
